// Acepta únicamente un entero opcionalmente seguido de UN separador
// decimal (coma o punto) y sus dígitos. Rechaza cualquier resto de texto
// (letras, unidades, separadores repetidos), a diferencia de
// Number.parseFloat, que ignora silenciosamente lo que no puede leer y
// aceptaría por ejemplo "120abc" o "12,3,4" como si fueran válidos.
const PATRON_DECIMAL = /^-?\d+([.,]\d+)?$/

// Normaliza un valor numérico ingresado por el usuario aceptando tanto
// coma como punto decimal (teclado numérico de celulares configurados en
// español suele insertar coma). Reutilizar esta función en vez de llamar
// directamente a Number.parseFloat sobre un input de usuario.
export function normalizarDecimal(valor) {
  if (typeof valor === 'number') {
    return Number.isFinite(valor) ? valor : NaN
  }

  if (valor === null || valor === undefined) return NaN

  const texto = String(valor).trim()

  if (!PATRON_DECIMAL.test(texto)) return NaN

  return Number(texto.replace(',', '.'))
}

// Acepta un monto en pesos ya con separador de miles chileno, opcional
// símbolo "$" y espacios. A diferencia de normalizarDecimal (pensada
// para decimales físicos como peso/tara, donde un solo punto SIEMPRE es
// separador decimal: "120.5" -> 120.5 kg), acá el punto es SIEMPRE
// separador de miles y la coma es el único separador decimal aceptado
// — la convención chilena real para dinero, sin ambigüedad:
//   "30000"       -> 30000
//   "30.000"      -> 30000   (NO 30 — sería el bug si se usara normalizarDecimal)
//   "$30.000"     -> 30000
//   "1.250.000"   -> 1250000
//   "30000,50"    -> 30000.5
// Usar exclusivamente para campos monetarios del POS (monto de pago,
// valor/kg, descuento en monto, monto pagado Cajas/Rejillas). NUNCA para
// peso bruto, tara o cantidad — esos siguen usando normalizarDecimal.
const PATRON_MONEDA = /^-?\d+(\.\d+)?$/

export function normalizarMoneda(valor) {
  if (typeof valor === 'number') {
    return Number.isFinite(valor) ? valor : NaN
  }

  if (valor === null || valor === undefined) return NaN

  let texto = String(valor).trim().replace(/\$/g, '').replace(/\s+/g, '')

  if (texto === '') return NaN

  if (texto.includes(',')) {
    const partes = texto.split(',')

    if (partes.length !== 2) return NaN

    texto = `${partes[0].replace(/\./g, '')}.${partes[1]}`
  } else if (texto.includes('.')) {
    texto = texto.replace(/\./g, '')
  }

  if (!PATRON_MONEDA.test(texto)) return NaN

  return Number(texto)
}

// Redondea a 2 decimales (centavos) usando el mismo criterio que
// Supabase aplica con `round(valor::numeric, 2)`. Imprescindible para
// comparar/sumar montos calculados en JS (float64) contra lo que el
// backend valida con `numeric(16,2)`: sumar líneas SIN redondear cada
// una antes puede arrastrar centavos de diferencia que solo se notan al
// comparar el total contra pagos que sí son montos "limpios".
export function redondearMoneda(valor) {
  const numero = Number(valor)

  if (!Number.isFinite(numero)) return 0

  return Math.round((numero + Number.EPSILON) * 100) / 100
}

// Helper central de presentación para TODO monto monetario de la app
// (POS, gastos, ingresos, dashboard). Antes cada pantalla llamaba
// `toLocaleString('es-CL', { maximumFractionDigits: 0 })` por su cuenta,
// lo que trunca visualmente los centavos que Supabase sí guarda
// (numeric(16,2)): 55498.50 se mostraba como "$55.499" y un saldo
// pendiente real de 0.50 se mostraba como "$1". NO usar
// maximumFractionDigits: 0 — es precisamente ese bug.
//
// Regla: si el valor tiene parte decimal, se muestran los 2 decimales
// completos (55498.5 -> "$55.498,50"); si es entero, sin decimales
// (55498 -> "$55.498"). Nunca redondea el valor en sí, solo cómo se
// presenta — para eso usar redondearMoneda antes de comparar/sumar.
export function formatearMoneda(valor) {
  const numero = redondearMoneda(valor)
  const centavos = Math.round(Math.abs(numero) * 100)
  const tieneCentavos = centavos % 100 !== 0

  return numero.toLocaleString('es-CL', {
    style: 'currency',
    currency: 'CLP',
    minimumFractionDigits: tieneCentavos ? 2 : 0,
    maximumFractionDigits: 2,
  })
}
